import "./DaCreateAccount.css";

import { useState, useMemo } from "react";
import DaPageWrapper from "../DaPageWrapper/DaPageWrapper";
import { Button } from "@mui/material";
import { Translate } from "../../helpers/TranslatedComponent";
import { T_KEYS } from "../../locales/translationKeys";
import { translateStatic } from "../../hooks/useT/useT";
import { useAppTheme } from "../../theme/useTheme";

const REGEX = {
  special: /[^A-Za-z0-9]/,
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  number: /[0-9]/,
};

const DaCreateAccount = () => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const theme = useAppTheme();

  const handleChange = (e) => {
    setPassword(e.target.value);
  };

  const toggleShowPassword = () => setShowPassword((s) => !s);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (allPasswordChecksMet) {
      setAttemptedSubmit(false);
    } else {
      setAttemptedSubmit(true);
    }
  };

  const getPasswordRequirements = (pw) => ({
    minLength: pw.length >= 10,
    specialChar: REGEX.special.test(pw),
    uppercase: REGEX.uppercase.test(pw),
    lowercase: REGEX.lowercase.test(pw),
    number: REGEX.number.test(pw),
  });

  const passwordChecks = useMemo(() => getPasswordRequirements(password), [password]);
  const allPasswordChecksMet =
    passwordChecks.minLength &&
    passwordChecks.number &&
    passwordChecks.specialChar &&
    passwordChecks.uppercase &&
    passwordChecks.lowercase;

  const inputClass = [
    "da-password-input",
    attemptedSubmit && !allPasswordChecksMet ? "error" : "",
    allPasswordChecksMet ? "valid" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderRequirement = (key, text, condition) => (
    <span key={key} className={`validation-messages ${condition ? "req-met" : "req-not-met"}`}>
      {condition ? (
        <img src="/ciam/v1/static/images/da/check-mark.svg" alt="check" />
      ) : attemptedSubmit ? (
        <img src="/ciam/v1/static/images/da/minus-icon.svg" alt="minus" />
      ) : (
        <img src="/ciam/v1/static/images/da/empty-box.svg" alt="cross" />
      )}
      {text}
    </span>
  );

  return (
    <DaPageWrapper
      wrapperClass={"da-createaccount-card da-page-wrapper"}
      iconType="lock_open"
      title={<Translate tKey={T_KEYS.DA.CREATE.TITLE} />}
      subtitle={<Translate tKey={T_KEYS.DA.PASSWORD.SUBTITLE} />}
    >
      <form className="da-createaccount-form" autoComplete="off" onSubmit={handleSubmit}>
        <label htmlFor="da-create-password" className="da-createaccount-label">
          <Translate tKey={T_KEYS.DA.PASSWORD.LABEL.PASSWORD} />
        </label>

        <div className="da-password-wrapper">
          <input
            id="da-create-password"
            name="createpassword"
            className={inputClass}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handleChange}
            autoFocus
          />

          <button
            type="button"
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="da-password-toggle"
            onClick={toggleShowPassword}
          >
            {showPassword ? (
              theme?.assets?.passwordHiddenIcon ? (
                <img
                  src={theme.assets.passwordHiddenIcon}
                  alt="Hide password"
                  className="da-password-visibility-icon"
                />
              ) : (
                <img
                  src="/ciam/v1/static/images/da/visibility.svg"
                  alt="Hide password"
                  className="da-password-visibility-icon"
                />
              )
            ) : theme?.assets?.passwordVisibleIcon ? (
              <img src={theme.assets.passwordVisibleIcon} alt="Show password" className="da-password-visibility-icon" />
            ) : (
              <img
                src="/ciam/v1/static/images/da/visibility_off.svg"
                alt="Show password"
                className="da-password-visibility-icon"
              />
            )}
          </button>
        </div>

        {attemptedSubmit && !allPasswordChecksMet && (
          <p className="da-password-validation">
            <img src="/ciam/v1/static/images/da/error.svg" alt="error" className="da-password-exclamation" />
            <span className="da-password-error-text">{translateStatic(T_KEYS.DA.PASSWORD.VALIDATION.PASSWORD)}</span>
          </p>
        )}

        <div className="da-password-reqs">
          {renderRequirement("length", translateStatic(T_KEYS.DA.PASSWORD.REQ.LENGTH), passwordChecks.minLength)}
          {renderRequirement("number", translateStatic(T_KEYS.DA.PASSWORD.REQ.NUMBER), passwordChecks.number)}
          {renderRequirement("special", translateStatic(T_KEYS.DA.PASSWORD.REQ.SPECIAL), passwordChecks.specialChar)}
          {renderRequirement(
            "case",
            translateStatic(T_KEYS.DA.PASSWORD.REQ.CASE),
            passwordChecks.uppercase && passwordChecks.lowercase
          )}
        </div>
        <Button className="da-button da-password-btn" type="submit" variant="contained" disableElevation>
          {translateStatic(T_KEYS.DA.PASSWORD.BUTTON.CONTINUE)}
        </Button>
      </form>
    </DaPageWrapper>
  );
};

export { DaCreateAccount };
